import React, { FunctionComponent, useState } from "react";
import branchOptions from "./options/branchOptions";
import subjectOptions from "./options/subjectOptions";
import Receipt from "./components/Receipt";
import ApiErrorMessage from "./components/ApiErrorMessage";

/* Validation */
import { Formik, useFormikContext } from "formik";
import validationSchema, { FormData } from "./formConfig";

/* API */
import axios from "axios";
import Endpoints from "@internal/common";
import { Button, Flex, Text, Title } from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";
import FormDropdown from "./components/FormDropdown";

const initialValue = {
  companyName: "",
  cvrNr: "",
  subject: "",
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  department: "",
};

const App = () => {
  const [status, setStatus] = useState<"apiError" | "submitted">();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const sendFormData = (formData: FormData) => {
    setStatus(undefined);
    setIsLoading(true);
    axios
      .post(Endpoints.SparkronErhverv, formData)
      .then(() => setStatus("submitted"))
      .catch(() => {
        setIsLoading(false);
        setStatus("apiError");
      });
  };

  return (
    <Flex center={"md"} fill layout={1}>
      {status === "submitted" ? (
        <Receipt />
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValue}
          validationSchema={validationSchema()}
          onSubmit={(formData: FormData) => {
            sendFormData(formData);
          }}
        >
          <RenderForm isLoading={isLoading} />
        </Formik>
      )}
      {status === "apiError" && <ApiErrorMessage />}
    </Flex>
  );
};

interface RenderProps {
  isLoading: boolean;
}

const RenderForm: FunctionComponent<RenderProps> = ({ isLoading }) => {
  const { handleSubmit, handleBlur, setFieldValue, touched, errors, values } =
    useFormikContext<FormData>();

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <Title size={4}>Få et uforpligtende tilbud på dine forsikringer</Title>
        <Text>
          Udfyld dine informationer i formularen, så kontakter en af vores
          dygtige rådgivere dig med et uforpligtende tilbud.
        </Text>

        <FormInput
          id="companyName"
          labelText="Virksomhedens navn"
          name="companyName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("companyName", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.companyName && errors.companyName)}
          errorText={errors.companyName}
          value={values.companyName}
        />
        <FormInput
          id="cvrNr"
          labelText="CVR-nummer"
          name="cvrNr"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("cvrNr", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.cvrNr && errors.cvrNr)}
          errorText={errors.cvrNr}
          value={values.cvrNr}
        />
        <FormDropdown
          id="subject"
          labelText="Hvad vil du tale med os om?"
          onChange={(values, value) => {
            setFieldValue("subject", value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.subject && errors.subject)}
          errorText={errors.subject}
          value={values.subject}
          options={subjectOptions}
        />
        <FormInput
          id="firstName"
          labelText="Dit fornavn"
          name="firstName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("firstName", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.firstName && errors.firstName)}
          errorText={errors.firstName}
          value={values.firstName}
        />

        <FormInput
          id="lastName"
          labelText="Dit efternavn"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("lastName", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.lastName && errors.lastName)}
          errorText={errors.lastName}
          value={values.lastName}
        />

        <FormInput
          id="email"
          labelText="Din email"
          type={"email"}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={values.email}
        />
        <FormInput
          id="phoneNumber"
          labelText="Dit telefonnummer"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("phoneNumber", event.target.value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.phoneNumber && errors.phoneNumber)}
          errorText={errors.phoneNumber}
          value={values.phoneNumber}
        />
        <FormDropdown
          id="department"
          labelText="Din afdeling"
          onChange={(values, value) => {
            setFieldValue("department", value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.department && errors.department)}
          errorText={errors.department}
          value={values.department}
          options={branchOptions}
        />
        <Button type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default App;
